/**
 * MTL材质文件导出器
 * 导出OBJ配套的材质文件，包含颜色信息
 */
import { Project, Element } from '../types';

/**
 * MTL材质导出器类
 */
export class MTLExporter {
  private output: string[];
  private materialMap: Map<string, MaterialInfo>;

  constructor() {
    this.output = [];
    this.materialMap = new Map();
  }

  /**
   * 导出项目的材质文件
   */
  exportProject(project: Project): string {
    this.output = [];
    this.materialMap.clear();

    // 添加文件头
    this.output.push('# Kimap MTL Material File');
    this.output.push(`# Project: ${project.name}`);
    this.output.push(`# Created: ${new Date().toISOString()}`);
    this.output.push('');

    // 添加隐藏底平面的材质（完全透明）
    this.output.push('# ==== Hidden Ground Plane Material ====');
    this.output.push('# This material makes the ground plane invisible');
    this.output.push('# The plane exists for raycasting but is not rendered');
    this.output.push('newmtl HiddenGroundPlane_Material');
    this.output.push('Ka 0.000000 0.000000 0.000000');
    this.output.push('Kd 0.000000 0.000000 0.000000');
    this.output.push('Ks 0.000000 0.000000 0.000000');
    this.output.push('Ns 0');
    this.output.push('d 0.0'); // 完全透明
    this.output.push('illum 0'); // 无光照
    this.output.push('# Special flag: __KIMAP_HIDDEN__');
    this.output.push('');

    // 遍历所有元素，收集材质信息
    project.floors.forEach((floor) => {
      floor.layers.forEach((layer) => {
        if (!layer.visible) return;

        layer.elements.forEach((element) => {
          this.collectMaterial(element);
        });
      });
    });

    // 导出所有材质
    this.materialMap.forEach((material, name) => {
      this.exportMaterial(name, material);
    });

    return this.output.join('\n');
  }

  /**
   * 收集元素的材质信息
   */
  private collectMaterial(element: Element): void {
    const materialName = this.getMaterialName(element);
    if (!materialName) return;

    const color = this.getElementColor(element);
    
    // 获取透明度（区域使用fillOpacity，其他元素不透明）
    let opacity = 1.0;
    if (element.type === 'area' && 'fillOpacity' in element) {
      const fillOpacity = (element as any).fillOpacity;
      opacity = fillOpacity !== undefined ? fillOpacity : 0.3;
      console.log(`🎨 区域透明度: ${element.id} -> ${opacity}`);
    }

    if (!this.materialMap.has(materialName)) {
      this.materialMap.set(materialName, {
        name: materialName,
        color: color,
        ambient: color,
        diffuse: color,
        specular: '#ffffff',
        shininess: 30,
        opacity: opacity,
        illumination: 2,
      });
    }
  }

  /**
   * 获取元素的材质名称
   */
  private getMaterialName(element: Element): string | null {
    switch (element.type) {
      case 'rectangle':
      case 'circle':
      case 'polygon':
        return `Shape_${element.type}_${element.id}`;
      case 'stair':
        return `Stair_${element.id}`;
      case 'wall':
        return `Wall_${element.id}`;
      case 'area':
        return `Area_${element.id}`;
      case 'door':
        return `Door_${element.id}`;
      default:
        return null;
    }
  }

  /**
   * 获取元素的颜色
   */
  private getElementColor(element: Element): string {
    let color: string;
    
    // 优先使用元素自身的颜色属性
    if ('color' in element && element.color) {
      color = element.color;
    } else {
      // 如果没有颜色属性，使用类型默认颜色
      switch (element.type) {
        case 'rectangle':
        case 'circle':
        case 'polygon':
          color = '#cccccc';
          break;
        case 'stair':
          color = '#ea580c'; // 楼梯橙色
          break;
        case 'wall':
          color = '#999999'; // 墙体灰色
          break;
        case 'area':
          color = '#e6e6e6'; // 区域浅灰
          break;
        case 'door':
          color = '#8B4513'; // 门褐色
          break;
        default:
          color = '#cccccc';
      }
    }
    
    // 墙体颜色与白色混合50%（与预览完全一致）
    if (element.type === 'wall') {
      const originalColor = color;
      color = this.mixColorWithWhite(color, 0.5);
      console.log(`🎨 墙体颜色混合: ${originalColor} -> ${color}`);
    }
    // 区域颜色不混合，直接使用原色（与预览保持一致）
    
    return color;
  }
  
  /**
   * 将颜色与白色混合
   */
  private mixColorWithWhite(hexColor: string, ratio: number): string {
    // 解析颜色
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // 与白色(255, 255, 255)混合
    const mixedR = Math.round(r + (255 - r) * ratio);
    const mixedG = Math.round(g + (255 - g) * ratio);
    const mixedB = Math.round(b + (255 - b) * ratio);
    
    // 转回十六进制
    return '#' + 
      mixedR.toString(16).padStart(2, '0') +
      mixedG.toString(16).padStart(2, '0') +
      mixedB.toString(16).padStart(2, '0');
  }

  /**
   * 导出单个材质
   */
  private exportMaterial(name: string, material: MaterialInfo): void {
    this.output.push(`newmtl ${name}`);
    
    // 解析颜色
    // const rgb = this.hexToRgb(material.color); // 未使用
    const ambientRgb = this.hexToRgb(material.ambient);
    const diffuseRgb = this.hexToRgb(material.diffuse);
    const specularRgb = this.hexToRgb(material.specular);
    
    // 环境光
    this.output.push(`Ka ${ambientRgb.r} ${ambientRgb.g} ${ambientRgb.b}`);
    
    // 漫反射
    this.output.push(`Kd ${diffuseRgb.r} ${diffuseRgb.g} ${diffuseRgb.b}`);
    
    // 镜面反射
    this.output.push(`Ks ${specularRgb.r} ${specularRgb.g} ${specularRgb.b}`);
    
    // 高光指数
    this.output.push(`Ns ${material.shininess}`);
    
    // 透明度
    this.output.push(`d ${material.opacity}`);
    
    // 光照模型
    this.output.push(`illum ${material.illumination}`);
    
    this.output.push('');
  }

  /**
   * 十六进制颜色转RGB
   */
  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    // 移除#号
    hex = hex.replace('#', '');
    
    // 解析RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    
    return {
      r: parseFloat(r.toFixed(6)),
      g: parseFloat(g.toFixed(6)),
      b: parseFloat(b.toFixed(6)),
    };
  }
}

/**
 * 材质信息接口
 */
interface MaterialInfo {
  name: string;
  color: string;
  ambient: string;
  diffuse: string;
  specular: string;
  shininess: number;
  opacity: number;
  illumination: number;
}

/**
 * 加密MTL内容为.kitheme格式
 */
export function encryptMTL(mtlContent: string): string {
  const ENCRYPTION_KEY = 'KIMAP_3D_MODEL_2024';
  
  try {
    // 1. 转换为字节数组
    const bytes = stringToBytes(mtlContent);
    
    // 2. XOR加密
    const encrypted = xorCipher(bytes, ENCRYPTION_KEY);
    
    // 3. Base64编码
    const base64 = base64Encode(encrypted);
    
    // 4. 添加文件头
    return 'THEME_V1\n' + base64;
  } catch (error) {
    throw new Error(`MTL加密失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

/**
 * 解密.kitheme文件为MTL内容
 */
export function decryptTheme(themeContent: string): string {
  const ENCRYPTION_KEY = 'KIMAP_3D_MODEL_2024';
  
  try {
    // 1. 检查文件头
    if (!themeContent.startsWith('THEME_V1\n')) {
      throw new Error('无效的Theme文件格式');
    }
    
    // 2. 移除文件头
    const base64Content = themeContent.substring('THEME_V1\n'.length);
    
    // 3. Base64解码
    const encrypted = base64Decode(base64Content);
    
    // 4. XOR解密
    const decrypted = xorCipher(encrypted, ENCRYPTION_KEY);
    
    // 5. 转换为字符串
    const mtlContent = bytesToString(decrypted);
    
    return mtlContent;
  } catch (error) {
    throw new Error(`Theme解密失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

// ===== 加密辅助函数 =====

function xorCipher(data: Uint8Array, key: string): Uint8Array {
  const keyBytes = stringToBytes(key);
  const result = new Uint8Array(data.length);
  
  for (let i = 0; i < data.length; i++) {
    result[i] = data[i] ^ keyBytes[i % keyBytes.length];
  }
  
  return result;
}

function stringToBytes(str: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

function bytesToString(bytes: Uint8Array): string {
  const decoder = new TextDecoder();
  return decoder.decode(bytes);
}

function base64Encode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64Decode(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * 导出项目的材质文件
 */
export function exportProjectAsMTL(project: Project): string {
  const exporter = new MTLExporter();
  return exporter.exportProject(project);
}
